import React, { FC, useState } from "react";
import { Menu, MenuItem, Typography } from "@mui/material";
import classNames from "classnames";
import { IconDot, IconPencil, IconTrash } from "../../../utils/icons";

interface Props {
    noteId: number;
    onEdit: () => void;
    onDelete: () => void;
}

// The ⋯ menu on a note (copied from the video-review CommentMenu). The caller
// renders it only on the viewer's own, non-deleted notes while the thread is
// open — the API allows edit/delete by the author only.
export const BriefNoteMenu: FC<Props> = ({ noteId, onEdit, onDelete }) => {
    const buttonId = `brief-note-menu-btn-${noteId}`;
    const menuId = `brief-note-menu-${noteId}`;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const closeMenu = () => setAnchorEl(null);

    const getOption = (Icon: any, text: string, callback: () => void) => (
        <MenuItem
            className="!p-3 !px-4 flex gap-2 items-center min-w-[220px]"
            onClick={() => {
                callback();
                closeMenu();
            }}
        >
            <Icon width={24} height={24} size={24} stroke="#B0B0B0" />
            <Typography color="var(--text-2)">{text}</Typography>
        </MenuItem>
    );

    return (
        <div onClick={(e) => e.stopPropagation()}>
            {/* Looks like the shared MenuIcon, but a real button: keyboard
                reachable, named, and wired to this note's menu. */}
            <button
                type="button"
                id={buttonId}
                aria-label="Note actions"
                aria-haspopup="menu"
                aria-controls={anchorEl ? menuId : undefined}
                aria-expanded={anchorEl ? "true" : undefined}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                className={classNames(
                    "flex items-center justify-center cursor-pointer border border-transparent hover:bg-surface-2 hover:border-muted p-1 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-accent",
                    { "!bg-surface-2 !border-muted": !!anchorEl }
                )}
            >
                <IconDot />
            </button>
            <Menu
                anchorEl={anchorEl}
                onClose={closeMenu}
                id={menuId}
                MenuListProps={{ "aria-labelledby": buttonId }}
                open={!!anchorEl}
                className="py-2"
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "left"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right"
                }}
            >
                {getOption(IconPencil, "Edit Note", onEdit)}
                {getOption(IconTrash, "Delete Note", onDelete)}
            </Menu>
        </div>
    );
};
